import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@hero/web-lib';
import {
  AlertCircleIcon,
  ArrowUpRightIcon,
  ChevronDownIcon,
  CircleCheckIcon,
  ClientIcon,
  InvoiceIcon,
  LoadMainIcon,
  LoaderIcon,
  type IconProps,
} from '@hero/web-icons';
import type { FC } from 'react';
import { heroEmblemWhite } from '@hero/web-assets';
import type { WidgetSpec } from '@hero/schema';
import type { AgentPresetIntent } from '@hero/api';
import { detectLanguageFromUserText } from '@hero/agent';
import type { Language } from '@hero/schema';
import { useAgentChatStore } from './store';
import { useAgentChatManager } from './useAgentChatManager';
import { ActivityChip } from './ActivityChip';
import { ActivityLogDrawer } from './ActivityLogDrawer';
import { WidgetRenderer } from './widgets/WidgetRenderer';
import { ConfirmationCard } from './ConfirmationCard';
import { ResumeStrip } from './ResumeStrip';
import { getConfirmationBlockers } from './getConfirmationBlockers';
import { useIsAnyCardBusy } from './useCardBusy';
import { AssistantMarkdown } from './AssistantMarkdown';
import { chatContentClasses, chatScrollClasses } from './chatScrollClasses';
import { summarizePendingBatch } from './summarizePendingBatch';
import {
  buildStagedCreateOrderSummaryParts,
  type StagedCreateOrderSummaryParts,
} from './summarizeStagedCreateOrder';
import type { ChatBubble } from './types';

/**
 * Conversation transcript.
 *
 * Visual model (intentionally Claude / Perplexity style):
 *
 * - **User messages** render as a contained dark pill, end-aligned —
 *   they read like sent SMS bubbles, which is the affordance users
 *   expect for *their own* speech.
 * - **Assistant messages** render with a small Hero emblem avatar on
 *   the inline-start side and flowing prose beside it. Activity chips
 *   and widgets sit in the content column next to the avatar.
 *
 * Auto-scrolls to the bottom on every new bubble / token / chip /
 * widget so the user always sees the latest output.
 */
export const MessageList = ({
  layout = 'floating',
}: {
  layout?: 'floating' | 'page';
}) => {
  const bubbles = useAgentChatStore(s => s.bubbles);
  const pendingConfirmations = useAgentChatStore(s => s.pendingConfirmations);
  const isMultiConfirmationStack = useAgentChatStore(s => s.isMultiConfirmationStack);
  const confirmationBatch = useAgentChatStore(s => s.confirmationBatch);
  const hasResumable = useAgentChatStore(s => !!s.resumable);
  const lastAssistantProse = useAgentChatStore(s => {
    for (let i = s.bubbles.length - 1; i >= 0; i--) {
      if (s.bubbles[i].role === 'assistant') return s.bubbles[i].text.trim();
    }
    return '';
  });
  const lastAssistantStreaming = useAgentChatStore(s => {
    for (let i = s.bubbles.length - 1; i >= 0; i--) {
      if (s.bubbles[i].role === 'assistant') return s.bubbles[i].streaming ?? false;
    }
    return false;
  });
  const lastUserText = useAgentChatStore(s => {
    for (let i = s.bubbles.length - 1; i >= 0; i--) {
      if (s.bubbles[i].role === 'user') return s.bubbles[i].text;
    }
    return '';
  });
  const containerRef = useRef<HTMLDivElement>(null);

  const batchSummary = useMemo(
    () =>
      pendingConfirmations.length > 0
        ? summarizePendingBatch(pendingConfirmations)
        : null,
    [pendingConfirmations],
  );

  const stagedOrderSummaryParts = useMemo(() => {
    if (
      pendingConfirmations.length !== 1 ||
      pendingConfirmations[0].action !== 'create_order' ||
      lastAssistantProse ||
      lastAssistantStreaming
    ) {
      return null;
    }
    return buildStagedCreateOrderSummaryParts(pendingConfirmations[0]);
  }, [pendingConfirmations, lastAssistantProse, lastAssistantStreaming]);

  /**
   * Renderable bubbles in chronological insertion order. Includes
   * confirmation-outcome bubbles (`confirm-*`) inline so that when the
   * user sends a new message after confirming, the outcome stays
   * **above** the new turn — not pinned to the bottom of the
   * transcript. `batch-progress-*` bubbles still render elsewhere.
   */
  const transcriptBubbles = useMemo(
    () => bubbles.filter(b => !b.id.startsWith('batch-progress-')),
    [bubbles],
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [
    bubbles,
    pendingConfirmations.length,
    confirmationBatch?.completed,
  ]);

  // A fresh panel keeps its starting screen — prompt chips and all —
  // even when the server still holds unanswered cards. Those are
  // offered by `ResumeStrip` and only join the thread on request.
  if (bubbles.length === 0 && pendingConfirmations.length === 0) {
    return (
      <div className={cn('flex flex-1 flex-col', chatScrollClasses)}>
        {hasResumable && (
          <div className={cn('pt-3', chatContentClasses(layout))}>
            <ResumeStrip />
          </div>
        )}
        <EmptyState layout={layout} />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn('flex-1 bg-white', chatScrollClasses)}
    >
      <div className={chatContentClasses(layout)}>
        <div className='flex flex-col gap-6'>
          {hasResumable && <ResumeStrip />}

          {transcriptBubbles.map(bubble => (
            <BubbleRow key={bubble.id} bubble={bubble} />
          ))}

          {pendingConfirmations.length > 0 && (
            <>
              {isMultiConfirmationStack && batchSummary ? (
                <PendingBatchSummaryRow summary={batchSummary.line} />
              ) : stagedOrderSummaryParts ? (
                <StagedCreateOrderSummaryRow
                  parts={stagedOrderSummaryParts}
                  userText={lastUserText}
                />
              ) : null}
              <ConfirmationStack
                count={pendingConfirmations.length}
                isMultiStack={isMultiConfirmationStack}
                batchSummary={batchSummary?.line}
              >
                {pendingConfirmations.map(pending => (
                  <ConfirmationCard
                    key={pending.id}
                    pending={pending}
                    stackCollapsed={isMultiConfirmationStack}
                  />
                ))}
              </ConfirmationStack>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const BubbleRow = ({ bubble }: { bubble: ChatBubble }) => {
  if (bubble.role === 'user') return <UserBubble bubble={bubble} />;
  return <AssistantBubble bubble={bubble} />;
};

/**
 * Assistant-style one-liner above a multi-card stack — "20 orders —
 * Berain 2, Arasco 3, and 15 others". Matches the avatar + prose
 * pattern so bulk review reads like a normal assistant turn, not a
 * form header buried inside the cards.
 */
const PendingBatchSummaryRow = ({ summary }: { summary: string }) => (
  <div className='flex w-full items-start gap-3'>
    <AssistantAvatar />
    <p className='mt-1.5 text-[15px] font-medium text-slate-800'>{summary}</p>
  </div>
);

/**
 * Deterministic one-liner when the model stages a solo create-order
 * card without emitting its own acknowledgement prose.
 */
const StagedCreateOrderSummaryRow = ({
  parts,
  userText,
}: {
  parts: StagedCreateOrderSummaryParts;
  userText: string;
}) => {
  const { t, i18n } = useTranslation('m-agent');
  const turnLang = detectLanguageFromUserText(
    userText,
    (i18n.language?.split('-')[0] ?? 'en') as Language,
  );
  const origin =
    parts.originLabel ||
    t('confirmation.stage_summary.origin_fallback', { defaultValue: 'pickup' });
  const destination =
    parts.destinationLabel ||
    t('confirmation.stage_summary.destination_fallback', {
      defaultValue: 'destination',
    });

  let key: string;
  if (parts.extraDestinationCount > 0) {
    key = 'confirmation.stage_summary.single_order_extra_dest_with_pickup';
  } else if (parts.pickupLabel) {
    key =
      parts.orderCount === 1
        ? 'confirmation.stage_summary.single_order_with_pickup'
        : 'confirmation.stage_summary.single_order_with_pickup_plural';
  } else {
    key =
      parts.orderCount === 1
        ? 'confirmation.stage_summary.single_order_no_pickup'
        : 'confirmation.stage_summary.single_order_no_pickup_plural';
  }

  const summary = t(key, {
    lng: turnLang,
    count: parts.orderCount,
    client: parts.clientName,
    origin,
    destination,
    pickup: parts.pickupLabel,
    extra: parts.extraDestinationCount,
  });

  return <PendingBatchSummaryRow summary={summary} />;
};

/**
 * Wrapper around the stack of `ConfirmationCard`s. When ≥2 cards are
 * pending (typical for a "2 orders for Berain + 2 orders for Arasco"
 * turn) it renders a sticky-feeling header with bulk Confirm-all /
 * Cancel-all buttons so the user doesn't have to chase each card
 * individually.
 *
 * Uses `isMultiStack` (sticky until the pending list drains) instead
 * of live `count >= 2` so the layout never flips mid Confirm-all.
 */
const ConfirmationStack = ({
  count,
  isMultiStack,
  batchSummary,
  children,
}: {
  count: number;
  isMultiStack: boolean;
  batchSummary?: string;
  children: React.ReactNode;
}) => {
  const { t } = useTranslation('m-agent');
  const manager = useAgentChatManager();
  const confirmationBatch = useAgentChatStore(s => s.confirmationBatch);
  const isLocked = manager.isConfirming;
  // A stack is only confirmable in bulk while every card in it is
  // complete — otherwise "Confirm all" would push through the very
  // cards whose own button is disabled.
  const hasIncompleteCard = useAgentChatStore(s =>
    s.pendingConfirmations.some(p => getConfirmationBlockers(p.action, p.payload).length > 0),
  );
  // Same rule one level up: one card still re-pricing (or with its
  // location picker open) holds the whole bulk confirm.
  const isAnyCardBusy = useIsAnyCardBusy();

  if (!isMultiStack) {
    return <div className='ms-11 flex max-w-[85%] flex-col gap-3'>{children}</div>;
  }

  const batchProgress =
    confirmationBatch && confirmationBatch.total > 0
      ? `${confirmationBatch.completed}/${confirmationBatch.total}`
      : null;

  return (
    <div className='ms-11 flex max-w-[85%] flex-col gap-2 rounded-2xl border border-slate-200 bg-slate-50/60 p-2.5'>
      <div className='flex flex-wrap items-center justify-between gap-2 px-1 py-0.5'>
        <div className='flex min-w-0 flex-col gap-0.5'>
          <div className='flex items-center gap-2 text-xs font-semibold text-slate-700'>
            {isLocked ? (
              <LoaderIcon className='size-3.5 animate-spin text-primary-600' />
            ) : (
              <CircleCheckIcon className='size-3.5 text-primary-600' />
            )}
            <span>
              {isLocked && confirmationBatch
                ? t('confirmation.batch.progress_title', {
                    done: confirmationBatch.completed,
                    total: confirmationBatch.total,
                    defaultValue: 'Creating {{done}}/{{total}}…',
                  })
                : t('confirmation.stack.title', {
                    count,
                    defaultValue: 'Review {{count}} actions',
                  })}
            </span>
          </div>
          {batchSummary && !confirmationBatch ? (
            <span className='truncate ps-5 text-[11px] text-slate-500'>{batchSummary}</span>
          ) : null}
        </div>
        {!isLocked ? (
          <div className='flex items-center gap-1.5'>
            <button
              type='button'
              onClick={manager.declineAll}
              disabled={isLocked}
              className='rounded-lg px-2.5 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50'
            >
              {t('confirmation.stack.decline_all', 'Cancel all')}
            </button>
            <button
              type='button'
              onClick={manager.confirmAll}
              disabled={isLocked || hasIncompleteCard || isAnyCardBusy}
              title={
                hasIncompleteCard
                  ? t('confirmation.blockers.stack_hint', {
                      defaultValue: 'Some cards are missing required details',
                    })
                  : isAnyCardBusy
                    ? t('confirmation.busy.stack_hint', {
                        defaultValue: 'Waiting for a card to finish updating',
                      })
                    : undefined
              }
              className='flex items-center gap-1 rounded-lg bg-primary-600 px-2.5 py-1 text-xs font-medium text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50'
            >
              <CircleCheckIcon className='size-3.5' />
              {t('confirmation.stack.confirm_all', 'Confirm all')}
            </button>
          </div>
        ) : batchProgress ? (
          <span className='text-xs font-medium tabular-nums text-slate-500'>
            {batchProgress}
          </span>
        ) : null}
      </div>
      <div className='flex flex-col gap-2'>{children}</div>
    </div>
  );
};

/**
 * User says X — short, contained, end-aligned dark pill.
 */
const UserBubble = ({ bubble }: { bubble: ChatBubble }) => (
  <AnimatePresence mode='popLayout'>
    <motion.div
      key={bubble.id}
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 320, damping: 28 }}
      className='flex w-full justify-end'
    >
      <div className='max-w-[80%] whitespace-pre-wrap rounded-2xl rounded-ee-md bg-slate-900 px-4 py-2.5 text-sm leading-relaxed text-white shadow-sm'>
        {bubble.text}
      </div>
    </motion.div>
  </AnimatePresence>
);

/**
 * Hero emblem — small bot avatar pinned to each assistant turn.
 */
const AssistantAvatar = () => {
  const { t } = useTranslation('m-agent');
  return (
    <div
      className='mt-0.5 flex size-8 flex-none items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-[#3532A7] via-[#3453D9] to-[#2563EB] shadow-sm shadow-[#3532A7]/25 ring-1 ring-[#3532A7]/10'
    >
      <img
        src={heroEmblemWhite}
        alt={t('header.title', 'Hero AI')}
        className='h-3.5 w-auto object-contain'
      />
    </div>
  );
};

/**
 * Assistant response — Hero avatar + prose column. Activities
 * (tool-trace chips) render above the text in chronological order.
 */
const AssistantBubble = ({ bubble }: { bubble: ChatBubble }) => {
  const { t } = useTranslation('m-agent');
  const [activityLogOpen, setActivityLogOpen] = useState(false);

  const allActivities = bubble.activities ?? [];
  const inlineActivities = allActivities.filter(a => a.status !== 'failed');
  const isBatchProgress = bubble.id.startsWith('batch-progress-');
  const visibleActivities = isBatchProgress
    ? inlineActivities
    : inlineActivities.slice(0, 3);
  const hiddenCount = allActivities.length - visibleActivities.length;

  const hasActivities = allActivities.length > 0;
  const hasText = !!bubble.text || !!bubble.streaming;
  const hasError = !!bubble.errorCode;
  const hasBody = hasActivities || hasText || hasError || bubble.widgets.length > 0;

  if (!hasBody) return null;

  return (
    <AnimatePresence mode='popLayout'>
      <motion.div
        key={bubble.id}
        layout
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 320, damping: 28 }}
        className='flex w-full items-start gap-3'
      >
        <AssistantAvatar />

        <div className='min-w-0 flex-1 flex flex-col gap-3'>
          {hasActivities && (
            <div className='flex flex-wrap items-center gap-1.5'>
              {visibleActivities.map(a => (
                <ActivityChip key={a.id} activity={a} />
              ))}
              {hiddenCount > 0 || allActivities.some(a => a.status === 'failed') ? (
                <button
                  type='button'
                  onClick={() => setActivityLogOpen(true)}
                  className='text-[11px] font-medium text-primary-700 hover:underline'
                >
                  {t('activity_log.view_all', 'View all tools')}
                  {hiddenCount > 0 ? ` (+${hiddenCount})` : ''}
                </button>
              ) : null}
            </div>
          )}

          {hasText && !hasError && (
            <div className='text-[15px] text-slate-800'>
              {bubble.text ? (
                isOrderCreatedOutcome(bubble) ? (
                  <div className='flex items-start gap-2.5'>
                    <CircleCheckIcon className='mt-0.5 size-5 shrink-0 text-emerald-600' strokeWidth={2} />
                    <div className='min-w-0 flex-1'>
                      <AssistantMarkdown content={bubble.text} />
                    </div>
                  </div>
                ) : (
                  <AssistantMarkdown content={bubble.text} />
                )
              ) : bubble.streaming ? (
                <StreamingStatus label={bubble.statusLabel} />
              ) : null}
            </div>
          )}

          {hasError && <AssistantErrorBlock errorCode={bubble.errorCode!} />}

          {bubble.widgets.length > 0 && (
            bubble.batchOutcomeCollapsed ? (
              <CollapsibleOrderWidgets widgets={bubble.widgets} />
            ) : (
              <div className='flex w-full flex-col gap-2'>
                {bubble.widgets.map((w, idx) => (
                  <WidgetRenderer key={idx} widget={w} />
                ))}
              </div>
            )
          )}
        </div>

        <ActivityLogDrawer
          isOpen={activityLogOpen}
          onClose={() => setActivityLogOpen(false)}
          activities={allActivities}
        />
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * In-bubble error fallback. Localized via `m-agent.json` →
 * `errors.<code>` so we never render a raw exception message in
 * the transcript.
 */
const AssistantErrorBlock = ({
  errorCode,
}: {
  errorCode: NonNullable<ChatBubble['errorCode']>;
}) => {
  const { t } = useTranslation('m-agent');
  return (
    <div className='flex items-start gap-2 rounded-xl border border-red-100 bg-red-50/60 px-3 py-2.5 text-sm text-red-800'>
      <AlertCircleIcon className='mt-0.5 size-4 flex-none text-red-500' />
      <span className='leading-6'>
        {t(
          `errors.${errorCode}`,
          t(
            'errors.unknown',
            'Something went wrong while processing your request. Please try again.',
          ),
        )}
      </span>
    </div>
  );
};

const isOrderCreatedOutcome = (bubble: ChatBubble): boolean =>
  /^Created \d+ orders?\b/i.test(bubble.text ?? '') &&
  bubble.widgets.some(w => w.type === 'order_card');

/**
 * Collapsed order-card list for multi-client Confirm-all outcomes.
 * Keeps the transcript scannable — one summary line, cards on demand.
 */
const CollapsibleOrderWidgets = ({ widgets }: { widgets: WidgetSpec[] }) => {
  const { t } = useTranslation('m-agent');
  const [expanded, setExpanded] = useState(false);
  const orderCount = widgets.filter(w => w.type === 'order_card').length;

  if (orderCount === 0) {
    return (
      <div className='flex w-full flex-col gap-2'>
        {widgets.map((w, idx) => (
          <WidgetRenderer key={idx} widget={w} />
        ))}
      </div>
    );
  }

  return (
    <div className='flex w-full flex-col gap-2'>
      <button
        type='button'
        onClick={() => setExpanded(v => !v)}
        className='flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2 text-start text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100'
      >
        <ChevronDownIcon
          className={cn('size-4 shrink-0 transition-transform', expanded && 'rotate-180')}
        />
        <span>
          {expanded
            ? t('confirmation.batch.hide_orders', 'Hide orders')
            : t('confirmation.batch.view_orders', {
                count: orderCount,
                defaultValue: 'View {{count}} orders',
              })}
        </span>
      </button>
      {expanded ? (
        <div className='flex flex-col gap-2'>
          {widgets.map((w, idx) => (
            <WidgetRenderer key={idx} widget={w} />
          ))}
        </div>
      ) : null}
    </div>
  );
};

const StreamingDots = () => (
  <span className='inline-flex items-center gap-1 text-slate-400'>
    <motion.span
      animate={{ opacity: [0.2, 1, 0.2] }}
      transition={{ duration: 1.2, repeat: Infinity, delay: 0 }}
      className='inline-block size-1.5 rounded-full bg-current'
    />
    <motion.span
      animate={{ opacity: [0.2, 1, 0.2] }}
      transition={{ duration: 1.2, repeat: Infinity, delay: 0.2 }}
      className='inline-block size-1.5 rounded-full bg-current'
    />
    <motion.span
      animate={{ opacity: [0.2, 1, 0.2] }}
      transition={{ duration: 1.2, repeat: Infinity, delay: 0.4 }}
      className='inline-block size-1.5 rounded-full bg-current'
    />
  </span>
);

/**
 * Pre-text streaming indicator — three dots, optionally followed by
 * a soft live status label from the latest `status_update` event
 * ("Thinking…" → "Checking orders…" → "Running Computing analytics…"
 * → "Composing reply…"). The label cross-fades when it changes so
 * the transition reads as continuous progress instead of a hard cut.
 */
const StreamingStatus = ({ label }: { label?: string }) => (
  <span className='inline-flex items-center gap-2 text-slate-400'>
    <StreamingDots />
    <AnimatePresence mode='wait' initial={false}>
      {label ? (
        <motion.span
          key={label}
          initial={{ opacity: 0, y: 2 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -2 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
          className='text-xs font-medium text-slate-500'
        >
          {label}
        </motion.span>
      ) : null}
    </AnimatePresence>
  </span>
);

/**
 * Empty-state quick-start prompts. Each one renders as a short
 * scannable chip ("Orders report"), but on click sends the longer
 * structured prompt to the agent so the assistant gets enough
 * context to actually produce a useful report on the first turn.
 */
interface EmptyStateSample {
  id: 'orders_summary' | 'top_clients' | 'invoices_report';
  icon: FC<IconProps>;
  labelFallback: string;
  promptFallback: string;
  /**
   * Intent the backend should route to directly — skips the
   * supervisor classifier roundtrip (~300–800 ms) since the chip
   * already declares its domain. See `agentSendMessageAPISchema`
   * `presetIntent` for the wire contract.
   */
  presetIntent: AgentPresetIntent;
}

const EMPTY_STATE_SAMPLES: EmptyStateSample[] = [
  {
    id: 'orders_summary',
    icon: LoadMainIcon,
    labelFallback: 'Orders summary',
    promptFallback: 'Show me my orders summary.',
    presetIntent: 'order',
  },
  {
    id: 'top_clients',
    icon: ClientIcon,
    labelFallback: 'Top clients',
    promptFallback:
      'Show me my top 5 clients this month with their most-used routes and the number of orders for today, this week, and this month per client.',
    presetIntent: 'order',
  },
  {
    id: 'invoices_report',
    icon: InvoiceIcon,
    labelFallback: 'Invoices report',
    promptFallback:
      "Give me an invoices report — what's paid, pending, partially paid, overdue, and cancelled this month.",
    presetIntent: 'invoice',
  },
];

const EmptyState = ({ layout }: { layout: 'floating' | 'page' }) => {
  const { t } = useTranslation('m-agent');
  const manager = useAgentChatManager();

  return (
    <div
      className={cn(
        'flex flex-1 flex-col items-center justify-center gap-6 bg-gradient-to-b from-slate-50/40 via-white to-white py-10 text-center',
        chatContentClasses(layout),
      )}
    >
      <motion.div
        initial={{ scale: 0.7, opacity: 0, rotate: -10 }}
        animate={{ scale: 1, opacity: 1, rotate: 0 }}
        transition={{ type: 'spring', stiffness: 220, damping: 18 }}
        className='relative flex size-20 items-center justify-center overflow-hidden rounded-3xl bg-gradient-to-br from-[#3532A7] via-[#3453D9] to-[#2563EB] shadow-xl shadow-[#3532A7]/30'
      >
        <img src={heroEmblemWhite} alt='Hero' className='h-9 w-auto object-contain' />
        <motion.div
          animate={{ scale: [1, 1.4, 1], opacity: [0.5, 0, 0.5] }}
          transition={{ duration: 3, repeat: Infinity }}
          className='absolute inset-0 rounded-3xl bg-white/20'
        />
      </motion.div>

      <div className='space-y-1'>
        <h3 className='text-lg font-semibold text-slate-900'>
          {t('empty.title', 'How can I help today?')}
        </h3>
        <p className='text-sm text-slate-500'>
          {t(
            'empty.subtitle',
            'I can pull reports, create orders, look up clients, save locations, and more. Just ask.',
          )}
        </p>
      </div>

      <div className='flex w-full max-w-sm flex-col gap-2'>
        {EMPTY_STATE_SAMPLES.map(sample => {
          const label = t(`empty.samples.${sample.id}.label`, sample.labelFallback);
          const prompt = t(`empty.samples.${sample.id}.prompt`, sample.promptFallback);
          const Icon = sample.icon;
          return (
            <button
              key={sample.id}
              type='button'
              onClick={() => manager.sendMessage(prompt, { presetIntent: sample.presetIntent })}
              className='group flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-4 py-3 text-start text-sm font-medium text-slate-700 shadow-sm transition-all hover:border-primary-200 hover:bg-primary-50/40 hover:shadow-md'
            >
              <span className='flex size-7 flex-none items-center justify-center rounded-lg bg-primary-50 text-primary-600 transition-colors group-hover:bg-primary-100'>
                <Icon className='size-4' />
              </span>
              <span className='flex-1'>{label}</span>
              <ArrowUpRightIcon className='size-3.5 flex-none text-slate-300 transition-all group-hover:translate-x-px group-hover:-translate-y-px group-hover:text-primary-600' />
            </button>
          );
        })}
      </div>
    </div>
  );
};
